import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSocket } from '../../../shared/socket/useSocket';
import { useToast } from '../../../shared/ui/Toast/ToastProvider';
import { getErrorMessage } from '../../../shared/errors/getErrorMessage';
import { toUserZoned, fromUserZoned } from '../../../shared/lib/tzDate';
import { calendarApi } from '../../calendar/api';
import { commentsApi } from '../../comments/api';
import {
    useComments, useCreateComment, useUpdateComment, useUpdateCommentMetadata,
    useReactToComment, useDeleteComment, commentsQueryKey, useMarkCommentsAsRead
} from '../../comments/hooks/useComments';
import { ReminderPopover } from '../components/ReminderPopover';
import { ReminderFormSchema } from '../schemas';

// Extraído de `ComponentTaskHoy.jsx` (02.1-b6-taskhoy-orchestrator.md, pasos D1+E1) -- el panel
// de comentarios completo (data + mutaciones + socket + drafts del editor TipTap + tabs) MÁS el
// wizard de recordatorio (D1), combinados en un solo hook: `onSaveReminder` lee `taskComments`/
// `activeReminderCommentId` (adjunta el recordatorio a un comentario existente si vino de ahí) y
// `submitTaskComment` lee `pendingReminder` (adjunta el recordatorio pendiente al comentario
// nuevo que lo dispara) -- separarlos hubiera requerido pasar un callback cruzado entre 2 hooks
// para la misma cadena de eventos que ya existía en el código original (mismo criterio que
// `useTaskCellPopovers.jsx`). Extensión .jsx: `renderReminderPopover` devuelve JSX.
//
// Fix de candado de adjuntos: el HTML del candado lo arma el backend (embeddedContent.js), no un
// componente React -- mismos íconos/tooltip que features/docs/hooks/useEmbeddedMedia.js para que
// el parche in-place quede idéntico a lo que el backend serviría en un reload completo.
const LOCK_ICON_CLOSED = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>';
const LOCK_ICON_OPEN = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"></rect><path d="M7 11V7a5 5 0 0 1 9.9-1"></path></svg>';
const PRIVATE_TOOLTIP = 'Los documentos son privados y no se recomienda compartir el link del fichero, se tiene que descargar y compartir el fichero.';
const PUBLIC_TOOLTIP = 'Imagen pública';

export function useTaskComments({ activeTask, user, userTz, systemUsers, translate }) {
    const toast = useToast();
    const queryClient = useQueryClient();

    // `task.comments` se absorbió por completo hacia `"Comment"` -- los comentarios/moments de
    // la tarea activa ya no viven embebidos en `activeTask`, se leen/escriben vía el mismo
    // endpoint compartido con docs/dataindex (`document_id = activeTask.id`).
    const { data: fetchedTaskComments } = useComments(activeTask?.id);
    const taskComments = fetchedTaskComments ?? [];
    const createCommentMutation = useCreateComment();
    const updateCommentMutation = useUpdateComment();
    const updateCommentMetadataMutation = useUpdateCommentMetadata();
    const reactToCommentMutation = useReactToComment();
    const deleteCommentMutation = useDeleteComment();
    const markCommentsAsReadMutation = useMarkCommentsAsRead();

    useEffect(() => {
        if (!user?.id || taskComments.length === 0) return;
        
        const unreadCommentIds = taskComments
            .filter(c => String(c.author_id) !== String(user.id))
            .filter(c => !c.reads?.some(r => String(r.user_id) === String(user.id)))
            .map(c => c.comment_id);
            
        if (unreadCommentIds.length > 0) {
            markCommentsAsReadMutation.mutate(unreadCommentIds);
        }
    }, [taskComments, user?.id]);

    useSocket(useMemo(() => {
        const taskId = activeTask?.id;
        const setComments = (updater) => {
            queryClient.setQueryData(commentsQueryKey(taskId), (prev) => updater(prev ?? []));
        };
        return {
            comment_added: (newComment) => {
                if (String(newComment.document_id) === String(taskId)) {
                    setComments(prev => (prev.find(c => c.comment_id === newComment.comment_id) ? prev : [...prev, newComment]));
                }
            },
            comment_updated: (updatedComment) => {
                if (String(updatedComment.document_id) !== String(taskId)) return;
                setComments(prev => prev.map(c => c.comment_id === updatedComment.comment_id ? { ...c, ...updatedComment } : c));
            },
            comment_deleted: (data) => {
                setComments(prev => prev.filter(c => c.comment_id !== data.comment_id));
            },
            comments_read: (data) => {
                const { commentIds, userId: readerId } = data;
                setComments(prev => prev.map(c => {
                    if (commentIds.includes(c.comment_id)) {
                        // Optimistically we only have user_id and nick/avatar would be missing if we just push here,
                        // but since the backend broadcasts this, we can at least push the user_id to prevent refetching loop.
                        // We will invalidate the query to get the full read object with avatar
                        queryClient.invalidateQueries(commentsQueryKey(taskId));
                    }
                    return c;
                }));
            }
        };
    }, [activeTask?.id, queryClient]));

    const textareaRef = useRef(null);
    const commentsListRef = useRef(null);
    const [taskCommentsTab, setTaskCommentsTab] = useState('comentarios'); // 'info', 'comentarios', 'team', 'docs', 'meets'

    // Estado del editor de comentarios (TipTap, ver comment-editor/) -- `expandedCommentEditor`
    // decide si el "espacio de trabajo" (modal) está abierto y para cuál flujo; los `*Draft*`
    // guardan el HTML en progreso en el padre (no en el hijo) para no perderlo al pasar de
    // inline a modal y viceversa -- cada toggle desmonta un TaskCommentEditor y monta otro.
    // `newCommentText`/`textareaRef` son estado muerto preexistente (confirmado con grep antes de
    // mover -- `setNewCommentText` nunca se llama, siempre queda `''`; `textareaRef` nunca se
    // adjunta a ningún elemento). Se preservan verbatim, documentados, mismo criterio que
    // `metrics`/`flowNodes` de `useTaskPipeline.js`.
    const [newCommentText, setNewCommentText] = useState('');
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [expandedCommentEditor, setExpandedCommentEditor] = useState(null); // null | 'create' | 'edit'
    const [createDraftContent, setCreateDraftContent] = useState('');
    const [createResetKey, setCreateResetKey] = useState(0);
    const [editDraftContent, setEditDraftContent] = useState('');
    const [taskSmartTags, setTaskSmartTags] = useState([]);

    const scrollToCommentsBottom = () => {
        try {
            if (commentsListRef.current) {
                const el = commentsListRef.current;
                el.scrollTop = el.scrollHeight;
            }
        } catch (e) {
            console.error("Error scrolling comments to bottom:", e);
        }
    };

    useEffect(() => {
        if (activeTask) {
            scrollToCommentsBottom();
            const t1 = setTimeout(scrollToCommentsBottom, 50);
            const t2 = setTimeout(scrollToCommentsBottom, 150);
            const t3 = setTimeout(scrollToCommentsBottom, 300);
            const t4 = setTimeout(scrollToCommentsBottom, 600);
            return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
        }
    }, [activeTask?.id, taskComments.length, taskCommentsTab]);

    // Reminder States
    const [reminderPopoverPos, setReminderPopoverPos] = useState({ bottom: 0, right: 0 });
    const [showReminderPopover, setShowReminderPopover] = useState(false);
    const [activeReminderCommentId, setActiveReminderCommentId] = useState(null);
    // slice 02.1 · b′b — `date`/`users` viven en `reminderForm` (react-hook-form + `zodResolver`,
    // `ReminderFormSchema` de `../schemas`) en vez de 2 `useState` sueltos. Sin reset después de
    // guardar (comportamiento preexistente: el próximo recordatorio arranca con la última fecha/
    // usuarios elegidos como default) -- se preserva tal cual.
    const reminderForm = useForm({
        resolver: zodResolver(ReminderFormSchema),
        defaultValues: { date: null, users: [] },
    });
    const reminderSelectedUsers = reminderForm.watch('users') || [];
    const reminderSelectedDate = reminderForm.watch('date');
    const [reminderSearchQuery, setReminderSearchQuery] = useState('');
    const [reminderFocusedOptionIdx, setReminderFocusedOptionIdx] = useState(0);
    const [reminderMonth, setReminderMonth] = useState(toUserZoned(new Date(), userTz).getMonth());
    const [reminderYear, setReminderYear] = useState(toUserZoned(new Date(), userTz).getFullYear());
    const [pendingReminder, setPendingReminder] = useState(null);
    // Integración main→dev, epic D1.4 — modo comentario-sugerencia estructurado del cuadro de trabajo.
    const [suggestionMode, setSuggestionMode] = useState(false);
    const [taskMode, setTaskMode] = useState(false);

    // Reminder Tooltip States
    const [hoveredReminderId, setHoveredReminderId] = useState(null);
    const [hoveredReminderData, setHoveredReminderData] = useState(null);
    const [reminderTooltipPos, setReminderTooltipPos] = useState({ top: 0, left: 0 });
    const reminderHoverTimeoutRef = useRef(null);

    // Los 3 handlers pasan de mutar `activeTask.comments[]` localmente + `updateTaskInDB` a
    // llamar el mismo endpoint compartido que ya usan docs/dataindex (`/api/comments`) -- el
    // socket `comment_updated`/`comment_deleted` (ver arriba) actualiza el cache de React Query
    // para todos los clientes, este no necesita optimismo local propio.
    const toggleCommentReaction = (commentId) => {
        if (!user?.id) return;
        reactToCommentMutation.mutate({ commentId, userId: user.id }, {
            onError: (err) => {
                console.error('Reaction failed', err);
                toast.error(getErrorMessage(err));
            },
        });
    };

    const deleteComment = (commentId) => {
        toast.prompt(translate('comments.deleteConfirm'), {
            confirmText: translate('actions.delete'),
            onConfirm: () => {
                deleteCommentMutation.mutate(commentId, {
                    onError: (err) => {
                        console.error('Delete failed', err);
                        toast.error(getErrorMessage(err));
                    },
                });
            },
        });
    };

    const saveEditedComment = (commentId, newContent) => {
        updateCommentMutation.mutate({ commentId, content: newContent }, {
            onError: (err) => {
                console.error('Update failed', err);
                toast.error(getErrorMessage(err));
            },
        });
    };

    // slice 02.1 · b.5d.2 — el toggle de privacidad de un adjunto en el feed de comentarios (era
    // un onClick inline en `ComponentTaskHoy`, ver `CommentItem.jsx`). `wrapper`/`toggleBtn` son
    // los nodos DOM que ya tocaba el handler original -- feedback visual inmediato sobre ese DOM
    // (puede ser el mismo nodo o ya haber sido reemplazado por un re-render ajeno, no importa, es
    // solo cosmético; lo que persiste de verdad es el `setQueryData` de abajo).
    const handleToggleMediaVisibility = (comment, mediaKey, newAccess, wrapper, toggleBtn) => {
        const originalContent = comment.content;
        commentsApi.toggleMediaVisibility(comment.comment_id, mediaKey, newAccess)
            .then((json) => {
                if (!json.success) return;
                const isPrivate = newAccess === 'private';

                wrapper.dataset.access = newAccess;
                wrapper.title = isPrivate ? PRIVATE_TOOLTIP : PUBLIC_TOOLTIP;
                if (wrapper.tagName === 'A') wrapper.href = json.url;
                const link = wrapper.querySelector('a');
                if (link) link.href = json.url;
                const media = wrapper.querySelector('img, video, .cy-embed-audio-mount');
                if (media) {
                    if (media.tagName === 'IMG' || media.tagName === 'VIDEO') media.src = json.url;
                    else media.dataset.src = json.url;
                }
                toggleBtn.innerHTML = isPrivate ? LOCK_ICON_CLOSED : LOCK_ICON_OPEN;

                // El PATCH ya persistió el cambio en el backend (sobre el content crudo con
                // placeholders [media:N], no el HTML resuelto que se ve acá). Reconstruye el
                // mismo cambio sobre `originalContent` (string capturado arriba, no el DOM) y lo
                // guarda en el cache de React Query para que sobreviva al próximo re-render.
                const parsed = new DOMParser().parseFromString(originalContent, 'text/html');
                const target = parsed.querySelector(`[data-media-key="${mediaKey}"]`);
                if (target) {
                    target.dataset.access = newAccess;
                    target.title = isPrivate ? PRIVATE_TOOLTIP : PUBLIC_TOOLTIP;
                    if (target.tagName === 'A') target.setAttribute('href', json.url);
                    const targetLink = target.querySelector('a');
                    if (targetLink) targetLink.setAttribute('href', json.url);
                    const targetMedia = target.querySelector('img, video, .cy-embed-audio-mount');
                    if (targetMedia) {
                        if (targetMedia.tagName === 'IMG' || targetMedia.tagName === 'VIDEO') targetMedia.setAttribute('src', json.url);
                        else targetMedia.setAttribute('data-src', json.url);
                    }
                    const targetToggle = target.querySelector('.cy-embed-toggle');
                    if (targetToggle) targetToggle.innerHTML = isPrivate ? LOCK_ICON_CLOSED : LOCK_ICON_OPEN;
                }
                const newContent = parsed.body.innerHTML;
                queryClient.setQueryData(commentsQueryKey(activeTask.id), (prev) =>
                    (prev ?? []).map(cm => cm.comment_id !== comment.comment_id ? cm : { ...cm, content: newContent })
                );

                toast.success(isPrivate
                    ? 'El fichero se ha hecho privado. El enlace es temporal y deja de funcionar después de un tiempo.'
                    : 'El fichero se ha hecho público. El enlace se puede compartir sin restricción.');
            })
            .catch((err) => toast.error(getErrorMessage(err)));
    };

    // --- Reminder Logic ---
    const handleReminderToggleUser = (userNick) => {
        const userObj = systemUsers.find(u => u.nick === userNick);
        if (!userObj) return;
        const prev = reminderForm.getValues('users') || [];
        const isSelected = prev.some(u => u.id === userObj.id);
        const next = isSelected
            ? prev.filter(u => u.id !== userObj.id)
            : [...prev, { id: userObj.id, nick: userObj.nick, avatar: (userObj['link-profile-img-thumb'] || userObj['link-profile-img']) }];
        // Sólo revalida (y por lo tanto sólo puede mostrar el mensaje de error) después de un
        // primer intento de guardar -- no molesta con el error mientras el usuario recién está
        // eligiendo, sólo si ya intentó guardar y le falta algo.
        reminderForm.setValue('users', next, { shouldValidate: reminderForm.formState.isSubmitted });
    };

    // `reminderForm.handleSubmit` valida contra `ReminderFormSchema` antes de llamar acá.
    const onSaveReminder = async ({ date: reminderSelectedDate, users: reminderSelectedUsers }) => {
        try {
            let reminderTitle = '';
            if (activeReminderCommentId) {
                const comment = taskComments.find(c => c.comment_id === activeReminderCommentId);
                reminderTitle = comment?.content || translate('comments.reminderDefaultTitle');
            } else {
                reminderTitle = newCommentText || translate('comments.reminderDefaultTitle');
            }
            if (reminderTitle.length > 50) {
                reminderTitle = reminderTitle.substring(0, 47) + '...';
            }

            // Fase timezones: el día viene del picker (reminderSelectedDate, Y/M/D nada más);
            // 20:00/21:00 son wall-clock-en-`userTz` -- `fromUserZoned` calcula el instante UTC
            // real para esa hora en el timezone configurado, no en el del navegador.
            const reminderLocal = new Date(reminderSelectedDate);
            reminderLocal.setHours(20, 0, 0, 0); // 8:00 PM
            const endReminderLocal = new Date(reminderLocal);
            endReminderLocal.setHours(21, 0, 0, 0); // 9:00 PM
            const reminderDate = fromUserZoned(reminderLocal, userTz);
            const endReminderDate = fromUserZoned(endReminderLocal, userTz);

            const eventPayload = {
                title: reminderTitle,
                description: translate('comments.reminderEventDescription', { title: activeTask?.title || translate('common.taskFallback') }),
                start_time: reminderDate.toISOString(),
                end_time: endReminderDate.toISOString(),
                all_day: false,
                calendar_id: user?.calendar_personal || null,
                created_by: user?.id,
                attendees: reminderSelectedUsers.map(u => ({ id: u.id, name: u.nick, 'link-profile-img': u.avatar })),
                type: 'recordatorio'
            };

            const data = await calendarApi.createEvent(eventPayload);
            const newReminder = {
                eventId: data.event?.id,
                users: reminderSelectedUsers,
                date: reminderSelectedDate.toISOString()
            };

            if (activeReminderCommentId) {
                // Antes mutaba `c.reminder` directo dentro de `task.comments[]` -- ahora mezcla
                // `{reminder: {...}}` en `metadata` vía el endpoint compartido.
                updateCommentMetadataMutation.mutate({ commentId: activeReminderCommentId, metadata: { reminder: newReminder } }, {
                    onError: (err) => {
                        console.error('Error attaching reminder to comment', err);
                        toast.error(getErrorMessage(err));
                    },
                });
                setActiveReminderCommentId(null);
                // Fix real (bug preexistente, confirmado con `git show HEAD~2`): esta rama nunca
                // reseteaba `showReminderPopover`, solo la rama `else` de abajo lo hacía. Como
                // `onOpenReminderPopover` (en el container) prende ambos flags a la vez al abrir
                // el recordatorio de un comentario existente, y `TaskCommentsPanel.jsx` renderiza
                // una SEGUNDA instancia del popover gateada solo por `showReminderPopover` (además
                // de la que gatea `CommentItem.jsx` por `activeReminderCommentId`), guardar dejaba
                // esa segunda instancia visible en pantalla indefinidamente.
                setShowReminderPopover(false);
            } else {
                setPendingReminder(newReminder);
                setShowReminderPopover(false);
            }
        } catch (e) {
            console.error('Error saving reminder event', e);
            toast.error(getErrorMessage(e));
        }
    };

    // slice 02.1 · b.3/b′b — el popover de recordatorio vive en `ReminderPopover.jsx` (estaba
    // duplicado como JSX en 2 sitios). Acá quedan `reminderForm` + `onSaveReminder`.
    const reminderFormErrorKey = reminderForm.formState.errors.date
        ? 'modal.reminderDateRequired'
        : reminderForm.formState.errors.users
            ? 'modal.reminderUsersRequired'
            : null;
    const renderReminderPopover = (onClose) => (
        <ReminderPopover
            onClose={onClose}
            pos={reminderPopoverPos}
            systemUsers={systemUsers}
            selectedUsers={reminderSelectedUsers}
            onToggleUser={handleReminderToggleUser}
            searchQuery={reminderSearchQuery}
            onSearchChange={setReminderSearchQuery}
            focusedIdx={reminderFocusedOptionIdx}
            onFocusedIdxChange={setReminderFocusedOptionIdx}
            month={reminderMonth}
            year={reminderYear}
            onPrevMonth={() => setReminderMonth(m => m === 0 ? 11 : m - 1)}
            onNextMonth={() => setReminderMonth(m => m === 11 ? 0 : m + 1)}
            selectedDate={reminderSelectedDate}
            onDaySelect={(d) => reminderForm.setValue('date', d, { shouldValidate: reminderForm.formState.isSubmitted })}
            onSave={reminderForm.handleSubmit(onSaveReminder)}
            error={reminderFormErrorKey ? translate(reminderFormErrorKey) : null}
        />
    );

    // Antes armaba el comentario a mano (id client-side, autor denormalizado) y lo insertaba
    // directo en `activeTask.comments[]` -- ahora usa `POST /api/comments` (`document_id =
    // activeTask.id`), el mismo endpoint compartido con docs/dataindex. `CreateComment.js` ya
    // notifica menciones (`data-uid="..."`) server-side con el mismo criterio que este
    // componente duplicaba a mano -- se quita el duplicado (evita una notificación doble por
    // mención). `html` viene de `TaskCommentEditor.onSubmit` (Enter o botón Enviar) -- el propio
    // editor ya limpia su contenido después de llamar esto; acá solo falta resetear el estado
    // que vive en el padre (draft/resetKey para el modal, smart tags, recordatorio pendiente).
    const submitTaskComment = (html) => {
        if (!activeTask) return;

        const authorId = user?.dbId || user?.id;

        // Integración main→dev, epic D1.4 — comentario-sugerencia estructurado (morado): se
        // marca en `metadata` y `CreateComment` (backend) ya acepta metadata arbitraria. La
        // bitácora del cuadro de trabajo los lee vía la subquery `structured_comments`.
        const metadata = {
            ...(pendingReminder ? { reminder: pendingReminder } : {}),
            ...(suggestionMode ? { is_suggestion: true, structured: true, type: 'suggestion' } : {}),
        };

        createCommentMutation.mutate({
            document_id: activeTask.id,
            author_id: authorId,
            content: html,
            anchor_type: 'task',
            metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
        }, {
            onError: (e) => {
                console.error('Error creating task comment', e);
                toast.error(getErrorMessage(e));
            },
        });

        setTaskMode(false);
        setSuggestionMode(false);
        setTaskSmartTags([]);
        setPendingReminder(null);
        setCreateDraftContent('');
        setCreateResetKey((k) => k + 1);
        if (expandedCommentEditor === 'create') setExpandedCommentEditor(null);
    };

    // Entra en modo sugerencia (desde el botón de la card de bitácora): cambia a la pestaña de
    // comentarios y fuerza el remount+foco del editor de creación.
    const enterSuggestionMode = useCallback(() => {
        setTaskMode(false);
        setSuggestionMode(true);
        if (taskCommentsTab !== 'comentarios') {
            setTaskCommentsTab('comentarios');
        }
        setTimeout(() => {
            const input = document.querySelector('.comment-write-card .ProseMirror');
            if (input) input.focus();
        }, 50);
    }, [taskCommentsTab]);

    const enterTaskMode = useCallback(() => {
        setSuggestionMode(false);
        setTaskMode(true);
        if (taskCommentsTab !== 'comentarios') {
            setTaskCommentsTab('comentarios');
        }
    }, [taskCommentsTab]);

    // slice 02.1 · b.5f — memoizado (§6 del sub-plan): antes se llamaba 1x directo por render
    // (era barato con pocos comentarios, pero recalculaba filter/sort/synthesize del feed en
    // cada tecla tipeada en cualquier parte del panel).
    const taskFilteredComments = useMemo(() => {
        try {
            if (!activeTask) return [];
            // Reemplaza el viejo `activeTask.comments[]` -- se leen por separado vía
            // `useComments(activeTask.id)`.
            const list = taskComments;
            if (!Array.isArray(list)) return [];

            switch (taskCommentsTab) {
                case 'team': {
                    const seen = new Set();
                    const team = [];
                    list.forEach(c => {
                        if (c && c.author_id && !seen.has(String(c.author_id))) {
                            seen.add(String(c.author_id));
                            team.push({
                                id: c.author_id,
                                name: c.author_name || translate('common.guest'),
                                avatar: c.author_avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${c.author_name || 'Inv'}`
                            });
                        }
                    });
                    return team;
                }
                case 'docs': {
                    return list.filter(c => c && typeof c.content === 'string' && (
                        c.content.includes('<img') ||
                        c.content.includes('<video') ||
                        c.content.includes('class="file-tag"')
                    ));
                }
                case 'meets':
                    return list.filter(c => c && typeof c.content === 'string' && c.content.includes('Reunión 📅'));
                case 'comentarios':
                default: {
                    const commentsArr = list.filter(Boolean);

                    // Synthesize Creation Moment
                    if (activeTask.created_at) {
                        const hasCreationMoment = commentsArr.some(c => c.comment_kind === 'moment' && c.metadata && c.metadata.type === 'creation');
                        if (!hasCreationMoment) {
                            // No se pudo resolver un creador real (falta created_by y tampoco
                            // matchea con supervisor/assignee, típico de tareas legacy) — se
                            // muestra explícitamente como desconocido, no se inventa un nombre
                            // de usuario real.
                            let creatorName = translate('comments.authorUnavailable');
                            let creatorAvatar = `https://ui-avatars.com/api/?name=%3F&background=666666&color=fff`;

                            let creatorId = activeTask.created_by;

                            if (typeof systemUsers !== 'undefined') {
                                if (!creatorId) {
                                    // Fallback: match by supervisor or assignee name if created_by is missing (legacy tasks)
                                    const fallback = systemUsers.find(u => u.nick === activeTask.supervisor || u.nick === activeTask.assignee);
                                    if (fallback) creatorId = fallback.id;
                                }

                                if (creatorId) {
                                    const creator = systemUsers.find(u => String(u.id) === String(creatorId));
                                    if (creator) {
                                        creatorName = creator.nick || creator.name || translate('comments.authorUnavailable');
                                        creatorAvatar = (creator['link-profile-img-thumb'] || creator['link-profile-img']) || creator.avatar || creatorAvatar;
                                    }
                                }
                            }

                            const creationMoment = {
                                comment_id: `creation-${activeTask.id}`,
                                author_id: creatorId || null,
                                author_name: creatorName,
                                author_avatar: creatorAvatar,
                                anchor_type: 'task',
                                comment_kind: 'moment',
                                content: '',
                                status: 'active',
                                created_at: activeTask.created_at,
                                metadata: { type: 'creation' }
                            };

                            commentsArr.unshift(creationMoment);
                        }
                    }

                    return commentsArr.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
                }
            }
        } catch (e) {
            console.error("Error filtering comments:", e);
            return [];
        }
    }, [taskComments, taskCommentsTab, activeTask?.id, activeTask?.created_at, systemUsers, translate]);

    // --- Reminder Tooltip Logic ---
    const handleReminderMouseEnter = (e, commentId, reminderObj) => {
        let parsedReminder = reminderObj;
        if (typeof reminderObj === 'string') {
            try {
                parsedReminder = JSON.parse(reminderObj);
            } catch (err) {}
        }
        if (typeof parsedReminder !== 'object' || !parsedReminder.eventId) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const pos = { top: rect.bottom + 5, left: rect.left };

        reminderHoverTimeoutRef.current = setTimeout(async () => {
            try {
                const data = await calendarApi.fetchEventById(parsedReminder.eventId);
                if (data.success && data.event) {
                    setHoveredReminderData(data.event);
                    setReminderTooltipPos(pos);
                    setHoveredReminderId(commentId);
                }
            } catch (err) {
                console.error("Error fetching reminder event details:", err);
            }
        }, 1000);
    };

    const handleReminderMouseLeave = () => {
        if (reminderHoverTimeoutRef.current) {
            clearTimeout(reminderHoverTimeoutRef.current);
            reminderHoverTimeoutRef.current = null;
        }
        setHoveredReminderId(null);
        setHoveredReminderData(null);
    };

    const handleReminderClick = (e, reminderObj) => {
        e.stopPropagation();

        let parsedReminder = reminderObj;
        if (typeof reminderObj === 'string') {
            try {
                parsedReminder = JSON.parse(reminderObj);
            } catch (err) {
                // Not a JSON string
            }
        }

        if (typeof parsedReminder !== 'object' || !parsedReminder.eventId) {
            toast.error(translate('errors.oldReminderNoEvent'));
            return;
        }

        const eventUrl = `https://colonny.xyz/calendar/${parsedReminder.eventId}`;

        const fallbackCopy = () => {
            const textArea = document.createElement("textarea");
            textArea.value = eventUrl;
            textArea.style.position = "fixed";
            textArea.style.left = "-9999px";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                const successful = document.execCommand('copy');
                if (successful) {
                    toast.success(translate('errors.linkCopied'));
                } else {
                    toast.error(translate('errors.copyFailed', { url: eventUrl }));
                }
            } catch (err) {
                console.error('Fallback: Oops, unable to copy', err);
                toast.error(translate('errors.eventUrlFallback', { url: eventUrl }));
            }
            document.body.removeChild(textArea);
        };

        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(eventUrl).then(() => {
                toast.success(translate('errors.linkCopied'));
            }).catch(err => {
                console.error('Error copying text async: ', err);
                fallbackCopy();
            });
        } else {
            fallbackCopy();
        }
    };

    return {
        taskComments, taskFilteredComments,
        textareaRef, commentsListRef,
        taskCommentsTab, setTaskCommentsTab,
        newCommentText, setNewCommentText,
        editingCommentId, setEditingCommentId,
        expandedCommentEditor, setExpandedCommentEditor,
        createDraftContent, setCreateDraftContent,
        createResetKey, setCreateResetKey,
        editDraftContent, setEditDraftContent,
        taskSmartTags, setTaskSmartTags,
        toggleCommentReaction, deleteComment, saveEditedComment, handleToggleMediaVisibility,
        submitTaskComment,
        suggestionMode, setSuggestionMode, enterSuggestionMode,
        taskMode, setTaskMode, enterTaskMode,

        reminderPopoverPos, setReminderPopoverPos,
        showReminderPopover, setShowReminderPopover,
        activeReminderCommentId, setActiveReminderCommentId,
        pendingReminder, renderReminderPopover,

        hoveredReminderId, hoveredReminderData, reminderTooltipPos,
        handleReminderMouseEnter, handleReminderMouseLeave, handleReminderClick,
    };
}
